/**
 * Head-based trace sampling. Sentry bills on trace volume, so the production
 * baseline is a cost control rather than a taste preference.
 *
 * This is a pure function on purpose: the rate table is the part a reviewer
 * needs to be able to read, and buried inside `Sentry.init()` nobody would.
 *
 * WHAT THIS CANNOT DO. The decision is made when the trace STARTS, so it cannot
 * sample in on "this one errored" or "this one was slow" - neither fact exists
 * yet. That is tail-based sampling and it requires a collector, which the
 * design deliberately does not run. Two things make the limitation survivable
 * and both must be preserved if this file is edited:
 *
 * - Sentry ERROR events are governed by `sampleRate`, not `tracesSampleRate`,
 *   and `sampleRate` is left at its default of 1.0. A dropped trace never means
 *   a dropped error.
 * - The routes where slowness actually lives are the paid-inference routes, and
 *   those are sampled at 1.0 by name below.
 */

export const PRODUCTION_BASELINE_RATE = 0.1;

/**
 * Name fragments that force full sampling. These are low volume and high
 * diagnostic value: every one of them either spends money on a provider call,
 * puts a job on the queue, or IS the job, and those are what someone reading a
 * trace is usually looking for.
 *
 * Matched as substrings of the root span name, which for a Next route handler
 * is of the form "POST /api/lesson/dictation/grade".
 *
 * LEAVES, NOT PREFIXES, and that is the whole design of this list. It used to
 * read `/api/lesson/`, `/api/teaching/`, `/api/words/` and
 * `/api/conversations/`, which forced 100% on roughly 37% of the API surface -
 * including progress writes, ink saves, deck reads and completion marks, none
 * of which spend a cent. That was already a cost decision made by accident;
 * with tracing enabled each of those traces now also carries Sentry's own
 * instrumentation spans, so the accident got more expensive. A route belongs
 * here only if it reaches a provider or a queue. Everything else takes the
 * baseline and is still fully covered for ERRORS, which `sampleRate` governs.
 *
 * Dynamic segments are avoided rather than spelled: `/postmortem` and
 * `/api/native/lesson/` match whether the span name carries Next's `[id]`
 * placeholder or a real id. Those two fragments therefore also catch
 * `/api/conversations/[id]/postmortem/phrases`,
 * `/api/native/sessions/[id]/postmortem` and
 * `/api/native/lesson/[lessonId]/flashcards`, none of which spend. That is
 * accepted collateral of not spelling dynamic segments, not an oversight.
 *
 * THE MEMBERSHIP RULE. This list is NOT "every route that spends money" - that
 * set is bigger than this one and is tracked elsewhere (see EXCLUDED CATEGORY
 * below). A route belongs here for one of two different reasons:
 *
 * 1. LOW-VOLUME AND WORTH A FULL TRACE. Its handler or a module it calls
 *    reaches a provider through the `@lib/ai` seam, and it is called rarely
 *    enough (once per lesson, not once per practice attempt) that sampling it
 *    at 100% is cheap. Follow the CALL, not the import: a route that imports a
 *    module which reaches a provider through some OTHER export it never calls
 *    is not a member on that basis alone.
 * 2. A JOB PRODUCER, so a dropped producer trace cannot orphan the job trace it
 *    starts. `tracesSampler` returns `0` outright when `parentSampled ===
 *    false`, and a worker job carries its parent's sampling decision across the
 *    queue (see `@lib/jobs/trace`), so a producer sampled out means the job it
 *    enqueued is unobservable too. This half of the rule is about trace
 *    continuity, not about the producer's own cost.
 *
 * Two routes look like members under rule 1 but are not - both confirmed by
 * reading the call, not the import:
 *
 * - `/api/lesson/dictation/finish` calls `refillBoardSlot(..., "DICTATION")`,
 *   and `refill.ts` returns before its `enqueue` for exactly that lesson type
 *   (dictation is pool-based). It produces no job.
 * - `/api/words/deck` imports `@lib/vocabulary/wordLookup`, which does reach a
 *   provider - but through `getOrCreateLookupContent`, not through the
 *   `ensureWordForDeck` this route calls, which is pure database work.
 *
 * Conversely `/api/native/review` IS a member despite reaching the provider two
 * hops down, via `loadDueVocabForReview` -> `getOrCreateLookupContent`.
 *
 * JOB PRODUCERS ON THIS LIST (rule 2), each verified against its source at the
 * call site named:
 *
 * - `/api/lesson/generate`, `/api/lesson/homework/generate`,
 *   `/api/curriculum/board/pin`, `/api/curriculum/board/regenerate` enqueue
 *   `lesson.generate` directly.
 * - `/api/lesson/complete` (`completeAttempt.ts` -> `refillBoardSlot`),
 *   `/api/lesson/comprehension-progress`, `/api/lesson/read-aloud-progress`,
 *   and `/api/lesson/reading-milestones` (all three via
 *   `saveComprehensionProgress.ts` -> `refillBoardSlot(..., "READING")`, fired
 *   only on the completion transition) and `/api/speaking/finish`
 *   (`finishSession.ts` -> `refillBoardSlot(..., "SPEAKING")`) each reach
 *   `refill.ts`'s `enqueue("lesson.generate", ...)` one hop out, for every
 *   lesson type except DICTATION (pool-based, see above). Each of these fires
 *   only once per completed attempt, which is what keeps it low volume despite
 *   being a completion-transition event on a high-traffic path.
 * - `/api/native/hub` calls `ensureDailySynthesis`, which sends
 *   `tutorMemorySynthesize` through `boss.send` directly rather than through
 *   `@lib/jobs/queue`'s `enqueue` wrapper - same orphaning risk, different
 *   call shape, so it is a member by the rule's intent even though it would
 *   slip a check that greps for `enqueue(` specifically.
 *
 * EXCLUDED CATEGORY, named so the next reader knows it was considered and not
 * missed: the ~17 routes under `src/app/activities/**` that reach a provider
 * (`src/web/src/test/helpers/paidRoutes.ts` derives the current count from the
 * import graph and documents its own method - e.g.
 * `circumlocution/api/describe`, `shadowing/api/{audio/[phraseId],start,take}`,
 * `teach-back/api/{debrief,turn}`, `voice-capsule/api/record`). They satisfy
 * rule 1's "reaches a provider" half but fail its "low volume" half on
 * purpose: they are the highest-volume paid routes in the product, one call
 * per practice attempt rather than one per lesson. Forcing them to 100% would
 * multiply trace volume by exactly the amount this narrowing exists to avoid.
 * They are left at `PRODUCTION_BASELINE_RATE`, which still yields ample traces
 * given their volume, and their errors are still fully captured regardless of
 * `tracesSampleRate` (see `sampleRate` above).
 *
 * THIS LIST IS A DELIBERATE SUBSET, NOT A CLAIM OF EXHAUSTIVE COVERAGE. Only
 * the routes named above (the `@lib/ai`-reaching low-volume set, the job
 * producers, and the excluded activity category) have been walked and
 * confirmed against their source as of this comment. Any route not mentioned
 * anywhere in this file has not been re-audited here; if you need to know
 * whether it spends, follow its call, don't assume this list already answered
 * it.
 *
 * Keep the literal spend tokens out of the prose here. `routeSpendCoverage` and
 * `routeAuthCoverage` scan file TEXT, comments included, so naming a provider
 * entry point with its open paren in this file makes every route that
 * transitively imports the sampler look paid.
 */
export const ALWAYS_SAMPLED_NAME_FRAGMENTS: readonly string[] = [
  // Live conversation and tutoring, all streaming provider calls.
  "/api/agent/stream",
  "/api/agent/conversation/stream",
  "/api/agent/conversation/debrief",
  "/api/agent/conversation/scenarios",
  "/api/tutor/chat",
  "/api/tutor/feedback",
  "/api/lesson/qa",
  "/postmortem",
  // Grading and generation on the lesson path.
  "/api/lesson/grade",
  "/api/lesson/dictation/grade",
  "/api/lesson/dictation/drill",
  "/api/lesson/dictation/sentences",
  "/api/lesson/comprehension-question",
  "/api/lesson/scene-image",
  "/api/speaking/evaluate",
  "/api/teaching/module",
  "/api/native/review",
  "/api/onboarding/complete",
  // Word and voice lookups, each one a provider round trip.
  "/api/words/lookup",
  "/api/words/contextual",
  "/api/vocabulary/translate",
  "/api/voice/speak",
  "/api/voice/transcribe",
  // Reaches the tts provider's `speak` through `ensurePassageAudioForContent`.
  // Dropped by the narrowing because the spend is one hop away in a lib rather
  // than in the handler; it is a paid TTS call either way.
  "/api/voice/passage-audio",
  // Job PRODUCERS. Worth full sampling because the trace they start is the one
  // that crosses the queue, which is the continuity this branch exists to give.
  "/api/lesson/generate",
  "/api/lesson/homework/generate",
  "/api/curriculum/board/pin",
  "/api/curriculum/board/regenerate",
  "/api/native/lesson/",
  // Completion-transition producers: each reaches `refillBoardSlot`, which
  // enqueues `lesson.generate` one hop out for every lesson type but DICTATION.
  // Low volume because each fires once per completed attempt, not once per try.
  "/api/lesson/complete",
  "/api/lesson/comprehension-progress",
  "/api/lesson/read-aloud-progress",
  "/api/lesson/reading-milestones",
  "/api/speaking/finish",
  // Sends `tutorMemorySynthesize` via `boss.send` directly (not the `enqueue`
  // wrapper) - same orphaning risk as the producers above, different call shape.
  "/api/native/hub",
  // And the jobs themselves, on the worker side of that carrier.
  "job ",
];

export type ConvoSamplingContext = {
  name?: string;
  parentSampled?: boolean;
};

function isProduction(): boolean {
  return process.env.NODE_ENV === "production";
}

export function tracesSampler(ctx: ConvoSamplingContext): number {
  // An inbound decision wins unconditionally. Without this a trace that began
  // on an iPhone would be re-diced at the server and arrive with holes in it,
  // which defeats the whole point of propagating context.
  //
  // KNOWN EXPOSURE, DELIBERATELY LEFT IN. `parentSampled` comes off the
  // inbound `sentry-trace` header, which is attacker-controlled: anyone who
  // can reach a route can pin `sampled=1` on every request and drive our trace
  // volume, and therefore our Sentry bill, past the 0.1 baseline. Do not
  // "fix" this by deleting the line - honouring the inbound decision is the
  // single thing that makes a trace whole across the client/server boundary,
  // which is the point of the whole spine.
  //
  // It is latent rather than live today: no Convo client sends trace headers
  // yet, so in production every trace starts at the server and `parentSampled`
  // is undefined on all of them. It stops being latent the moment sub-projects
  // 3 and 4 make the browser and the iOS app propagate context. THE FIX BELONGS
  // THERE: gate this branch on the request being authenticated, so an unpinned
  // stranger cannot force sampling, while a signed-in learner's own trace still
  // arrives whole. That needs request-scoped auth state the sampler cannot see
  // from here, which is why it is not done in this file today.
  if (ctx.parentSampled === true) return 1;
  if (ctx.parentSampled === false) return 0;

  if (!isProduction()) return 1;

  const name = ctx.name ?? "";
  if (ALWAYS_SAMPLED_NAME_FRAGMENTS.some((fragment) => name.includes(fragment))) return 1;

  return PRODUCTION_BASELINE_RATE;
}
